// Authored PC catalog metadata. An ordered area list is not a runtime exit graph.
const key = value => String(value).toLowerCase();
export function normalizeLevelPath(value) {
  const path = String(value).replaceAll('\\', '/');
  if (!path || path.startsWith('/') || path.includes(':') || path.split('/').some(p => !p || p === '.' || p === '..'))
    throw new Error('Invalid catalog asset path');
  return path.toUpperCase();
}

function lines(text, label) {
  if (typeof text !== 'string' || text.length > 16 * 1024 * 1024) throw new Error(`Invalid ${label} text`);
  const result = [];
  for (const [index, line] of text.replace(/^\uFEFF/, '').split(/\r?\n/).entries()) {
    const tokens = []; let i = 0;
    while (i < line.length) {
      while (/\s/.test(line[i] ?? '') && i < line.length) i++;
      if (i >= line.length || line[i] === ';' || line.slice(i, i + 2) === '//') break;
      // NuFParGetWord toggles quotes, strips them, and accepts EOL while
      // quoted. Original files contain both omitted and doubled end quotes.
      let token = '', quoted = false;
      while (i < line.length) {
        const char = line[i];
        if (char === '"') { quoted = !quoted; i++; continue; }
        if (!quoted && (/\s|,/.test(char) || char === ';' || line.slice(i, i + 2) === '//')) break;
        token += char; i++;
      }
      tokens.push(token);
      if (line[i] === ',') i++;
    }
    if (tokens.length) result.push({ command: key(tokens[0]), args: tokens.slice(1), line: index + 1 });
  }
  return result;
}

function blocks(text, type, diagnostics) {
  const result = []; let current = null;
  for (const entry of lines(text, type)) {
    if (entry.command === `${type}_start`) {
      if (current || entry.args.length) throw new Error(`Invalid nested ${type} block at line ${entry.line}`);
      current = { line: entry.line, entries: [] };
    } else if (entry.command === `${type}_end`) {
      if (!current || entry.args.length) throw new Error(`Unmatched ${type} end at line ${entry.line}`);
      result.push(current); current = null;
    } else if (current) current.entries.push(entry);
    else diagnostics.push({ source: type, line: entry.line, reason: 'directive outside block', command: entry.command });
  }
  if (current) throw new Error(`Unclosed ${type} block at line ${current.line}`);
  return result;
}

function values(block, command) { return block.entries.filter(e => e.command === command); }
function one(block, command, required = false) {
  const found = values(block, command);
  if (found.length > 1 || (required && found.length !== 1)) throw new Error(`Expected one ${command} at block line ${block.line}`);
  if (!found.length) return null;
  if (found[0].args.length !== 1 || !found[0].args[0]) throw new Error(`Invalid ${command} at line ${found[0].line}`);
  return found[0].args[0];
}
function integer(value, label, min = -1) {
  if (!/^-?\d+$/.test(value) || !Number.isSafeInteger(Number(value)) || Number(value) < min) throw new Error(`Invalid ${label}`);
  return Number(value);
}
function numberField(block, name) { const v = one(block, name); return v === null ? null : integer(v, name); }
function references(block, command) {
  return values(block, command).map(e => {
    if (e.args.length !== 1 || !e.args[0]) throw new Error(`Invalid ${command} reference at line ${e.line}`);
    return key(e.args[0]);
  });
}
function textID(block) {
  const found = values(block, 'text_id');
  if (!found.length) return null;
  if (found.length !== 1 || found[0].args.length < 1 || found[0].args.length > 2) throw new Error('Invalid text_id');
  return found[0].args.map(v => integer(v, 'text_id'));
}
function flags(block) { return block.entries.filter(e => !e.args.length).map(e => e.command); }
function indexUnique(entries, label) {
  const map = new Map();
  for (const entry of entries) {
    if (map.has(entry.id)) throw new Error(`Duplicate ${label} ${entry.id}`);
    map.set(entry.id, entry);
  }
  return map;
}

export function parseAreaSettings(text) {
  const entries = lines(text, 'area settings'), characters = [], unknown = [];
  let storyCoins = null, freeplayCoins = null;
  for (const entry of entries) {
    if (entry.command === 'character') {
      if (entry.args.length < 2 || !['player', 'resident', 'cutscene'].includes(key(entry.args[1])))
        throw new Error(`Unsupported area character declaration at line ${entry.line}`);
      characters.push({ id: key(entry.args[0]), authoredName: entry.args[0], role: key(entry.args[1]), arguments: entry.args.slice(2) });
    } else if (['story_coins', 'freeplay_coins'].includes(entry.command)) {
      if (entry.args.length !== 1) throw new Error(`Invalid ${entry.command}`);
      const value = integer(entry.args[0], entry.command, 0);
      if (entry.command === 'story_coins') {
        if (storyCoins !== null) throw new Error('Duplicate story_coins'); storyCoins = value;
      } else { if (freeplayCoins !== null) throw new Error('Duplicate freeplay_coins'); freeplayCoins = value; }
    } else unknown.push(entry);
  }
  return { characters, storyCoins, freeplayCoins, trueJediTargets: { story: storyCoins, freeplay: freeplayCoins }, unknown };
}

function parseMissions(text) {
  const missions = []; let party = [], pickups = null;
  for (const entry of lines(text, 'missions')) {
    const a = entry.args;
    if (entry.command === 'party') { party = a.map(key); continue; }
    if (entry.command === 'pickups') {
      if (a.length !== 1 || !['on', 'off'].includes(key(a[0]))) throw new Error('Invalid missions pickups');
      pickups = key(a[0]) === 'on'; continue;
    }
    if (entry.command !== 'mission' || a.length !== 13 || key(a[0]) !== 'find' || key(a[2]) !== 'in_level' ||
      key(a[4]) !== 'time' || key(a[6]) !== 'bounty' || key(a[9]) !== 'name_id' || key(a[11]) !== 'text_id') {
      throw new Error(`Unsupported mission declaration at line ${entry.line}`);
    }
    missions.push({ target: key(a[1]), levelId: key(a[3]), timeSeconds: integer(a[5], 'mission time', 0),
      bounty: [integer(a[7], 'bounty', 0), integer(a[8], 'bounty', 0)], nameID: integer(a[10], 'mission name_id'), textID: integer(a[12], 'mission text_id') });
  }
  return { party, pickups, missions };
}

export function parseLevelCatalog({ areas: areasText, episodes: episodesText, levels: levelsText, missions: missionsText, areaFiles = {} }) {
  const diagnostics = [];
  const levels = indexUnique(blocks(levelsText, 'level', diagnostics).map(block => {
    const file = one(block, 'file', true), dir = one(block, 'dir', true), id = key(file), levelFlags = flags(block);
    const basePath = normalizeLevelPath(`LEVELS/${dir}/${file}`);
    const kind = ['intro', 'midtro', 'cutscene', 'outro', 'status'].find(kind => levelFlags.includes(`${kind}_level`)) ?? 'gameplay';
    return { id, file, dir, flags: levelFlags, kind, basePath, scenePath: `${basePath}_PC.GSC`, metadataPath: `${basePath}.TXT`, directives: block.entries };
  }), 'level');
  const settingsMap = new Map((areaFiles instanceof Map ? [...areaFiles] : Object.entries(areaFiles)).map(([path, text]) => [normalizeLevelPath(path), text]));
  const areas = indexUnique(blocks(areasText, 'area', diagnostics).map(block => {
    const file = one(block, 'file', true), dir = one(block, 'dir', true), id = key(file), areaFlags = flags(block);
    const levelIds = references(block, 'level'), metadataPath = normalizeLevelPath(`LEVELS/${dir}/${file}.TXT`);
    const orderedLevels = levelIds.map(levelID => {
      const level = levels.get(levelID);
      if (!level) diagnostics.push({ source: 'area', id, reason: 'unresolved level', levelId: levelID });
      return level ?? { id: levelID, unresolved: true };
    });
    const settings = settingsMap.has(metadataPath) ? parseAreaSettings(settingsMap.get(metadataPath)) : null;
    return { id, file, dir, flags: areaFlags, levelIds, levels: orderedLevels, metadataPath, settings,
      nameID: numberField(block, 'name_id'), textID: textID(block), minikit: one(block, 'minikit'), redBrickCheat: one(block, 'redbrick_cheat'),
      timeTrialSeconds: numberField(block, 'timetrial_time'), entryLevelId: levelIds[0] ?? null,
      firstGameplayLevelId: orderedLevels.find(l => l.kind === 'gameplay')?.id ?? null,
      authoredSequence: levelIds.slice(1).map((to, i) => ({ from: levelIds[i], to, kind: 'catalog-order' })), directives: block.entries };
  }), 'area');
  const episodes = blocks(episodesText, 'episode', diagnostics).map((block, i) => {
    const areaIds = references(block, 'area');
    const orderedAreas = areaIds.map(id => { if (!areas.has(id)) throw new Error(`Episode ${i + 1} references unknown area ${id}`); return areas.get(id); });
    const chapters = orderedAreas.filter(a => !a.flags.includes('ending_area') && !a.flags.includes('bonus_area') && !a.flags.includes('super_bonus_area'));
    return { number: i + 1, nameID: numberField(block, 'name_id'), textID: textID(block), areaIds, areas: orderedAreas, chapters,
      endingAreas: orderedAreas.filter(a => a.flags.includes('ending_area')), bonusAreas: orderedAreas.filter(a => a.flags.includes('bonus_area') || a.flags.includes('super_bonus_area')) };
  });
  const chapters = episodes.flatMap(e => e.chapters), membership = new Set(episodes.flatMap(e => e.areaIds));
  const extras = [...areas.values()].filter(a => !membership.has(a.id));
  const bonusAreas = [...areas.values()].filter(a => a.flags.includes('bonus_area') || a.flags.includes('super_bonus_area'));
  const missionData = parseMissions(missionsText);
  for (const mission of missionData.missions) if (!levels.has(mission.levelId)) diagnostics.push({ source: 'mission', reason: 'unresolved level', levelId: mission.levelId });
  return { episodes, chapters, areas, levels, bonusAreas, extras, ...missionData, diagnostics,
    counters: { episodes: episodes.length, chapters: chapters.length, areas: areas.size, levels: levels.size, bonusAreas: bonusAreas.length,
      endingAreas: [...areas.values()].filter(a => a.flags.includes('ending_area')).length, missions: missionData.missions.length,
      gameplayLevels: [...levels.values()].filter(l => l.kind === 'gameplay').length,
      areaSettingsLoaded: [...areas.values()].filter(a => a.settings).length } };
}
